import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Bot,
  Clock,
  DollarSign,
  MessageSquare,
  Users,
  Wand2,
  ArrowRight,
  Loader2,
  TrendingUp,
  Layers,
  Download,
  FileText,
  CheckCircle2,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import TopNav from "@/components/TopNav";
import { CompaniesSection } from "@/components/CompaniesSection";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getDashboard, dashboardExcelUrl, dashboardPdfUrl } from "@/lib/api";
import SavingsAssumptions, { ESTIMATE_TEXT } from "@/components/SavingsAssumptions";
import { useAuth } from "@/contexts/AuthContext";

const WINDOWS = [
  { v: 1, label: "1 mes" },
  { v: 3, label: "3 meses" },
  { v: 6, label: "6 meses" },
  { v: 9, label: "9 meses" },
  { v: 12, label: "12 meses" },
];

const MONTH_LABEL = (k) => {
  const [y, m] = k.split("-");
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleString("es-419", { month: "short" }).replace(".", "") + " " + y.slice(2);
};

export default function Dashboard() {
  const { user } = useAuth();
  const isStaff = ["admin", "consultor"].includes(user?.role);
  const [window, setWindow] = useState(() => Number(new URLSearchParams(globalThis.location.search).get("ventana")) || 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0); // Fix 8: refetch after adjusting assumptions
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const d = await getDashboard(window);
        if (!cancelled) { setData(d); setLoadError(""); }
      } catch (e) {
        // Fix 7 (N12): an error is not "no data"
        if (!cancelled) setLoadError(e?.response?.data?.detail || "No se pudo cargar el panel. Recargue la página.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [window, reloadKey]);

  const trend = useMemo(
    () =>
      (data?.trend || []).map((t) => ({
        ...t,
        label: MONTH_LABEL(t.month),
      })),
    [data]
  );

  const totals = data?.totals || {};
  // fix 11: the server omits hours/money/assumptions for non-master company users
  const seesSavings = data?.can_see_savings !== false;
  const agents = data?.agents || [];
  const byDept = data?.department_breakdown || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900" data-testid="dashboard-page">
      <TopNav
        right={
          <div className="flex items-center gap-2">
            <a href={dashboardExcelUrl(window)} download data-testid="dash-excel-btn" className="hidden md:block">
              <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                <Download className="mr-2 h-3.5 w-3.5" /> Excel
              </Button>
            </a>
            <a href={dashboardPdfUrl(window)} download data-testid="dash-pdf-btn" className="hidden md:block">
              <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                <FileText className="mr-2 h-3.5 w-3.5" /> PDF
              </Button>
            </a>
            <Link to="/consulta" className="hidden sm:block">
              <Button className="rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-slate-800" data-testid="new-diagnostic-btn">
                <Wand2 className="mr-2 h-3.5 w-3.5" /> Nuevo diagnóstico
              </Button>
            </Link>
          </div>
        }
      />

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        {loadError && (
          <p className="mb-6 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="dashboard-error">
            {typeof loadError === "string" ? loadError : "No se pudo cargar el panel."}
          </p>
        )}
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow text-accent-blue">Inviktus · Dashboard</p>
            <h1 className="mt-3 font-display text-4xl font-medium text-slate-900 sm:text-5xl">
              Métricas de Agentes IA
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-600">
              Métricas operativas y de impacto económico de todos los agentes generados a partir
              de sus diagnósticos ABC. La ventana de verificación es configurable de 1 a 12 meses.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Ventana</span>
            <Select value={String(window)} onValueChange={(v) => setWindow(Number(v))}>
              <SelectTrigger
                className="h-10 w-[150px] rounded-sm border-slate-300 bg-white text-xs uppercase tracking-[0.18em]"
                data-testid="window-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WINDOWS.map((w) => (
                  <SelectItem key={w.v} value={String(w.v)} data-testid={`window-opt-${w.v}`}>
                    {w.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {data?.scope?.restricted && !data.scope.company_slug && (
          <div className="mt-8 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800" data-testid="dash-no-company">
            Su usuario todavía no tiene una empresa asignada. Cuando un administrador la asigne, verá aquí
            las métricas de los agentes de su empresa.
          </div>
        )}

        <CompaniesSection />

        {/* KPI tiles (fix 11: hours and money only for staff and the company master) */}
        <section className={`mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 ${seesSavings ? "lg:grid-cols-6" : "lg:grid-cols-4"}`} data-testid="kpi-tiles">
          <Kpi icon={Bot}     label="Agentes"      value={totals.agents ?? 0} accent="teal" />
          <Kpi icon={MessageSquare} label="Interacciones" value={totals.chats ?? 0} accent="navy" />
          <Kpi icon={CheckCircle2} label="Tareas completadas" value={totals.tasks_completed ?? 0} accent="teal" />
          {seesSavings && <Kpi icon={Clock}    label="Horas ahorradas (estimado)" value={(totals.hours_saved ?? 0).toLocaleString("es-419")} suffix="h" accent="teal" />}
          {/* fix 9: one currency → its total; several → one subtotal per currency, never added up */}
          {!seesSavings ? null : totals.currency || !(data?.savings_by_currency || []).length ? (
            <Kpi icon={DollarSign} label={`Ahorro ${totals.currency || "USD"} (estimado)`} value={(totals.usd_saved ?? 0).toLocaleString("es-419", { maximumFractionDigits: 0 })} accent="teal" />
          ) : (
            <Kpi icon={DollarSign} label="Ahorro (estimado), por moneda" accent="teal"
              value={(data.savings_by_currency || []).map((c) => `${c.currency} ${Number(c.usd_saved || 0).toLocaleString("es-419", { maximumFractionDigits: 0 })}`).join(" · ")} />
          )}
          <Kpi icon={Layers}    label="Cobertura starters" value={(totals.starter_coverage_pct ?? 0)} suffix="%" accent="navy" />
        </section>
        {seesSavings && <p className="mt-3 text-xs text-slate-500" data-testid="dashboard-estimate-note">
          {ESTIMATE_TEXT} Los supuestos de cada agente (minutos por consulta y tarifa por hora) se ven en su tarjeta
          {data?.can_edit_metrics ? " y puede ajustarlos con «Ajustar»." : "."} «(por defecto)» = valor por defecto, no
          informado por el cliente en la consultoría.
          {!totals.currency && (data?.savings_by_currency || []).length > 1 && (
            <> Hay agentes en varias monedas: el ahorro se muestra por moneda y no se suman monedas distintas
              (no se aplica ningún tipo de cambio).</>
          )}
        </p>}

        {/* Fix 8: «¿Le resolvió esto?» votes of the users */}
        <section className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="dashboard-resolved-tiles">
          <Kpi icon={MessageSquare} label="Consultas valoradas" value={totals.rated_chats ?? 0} />
          <Kpi icon={CheckCircle2} label="Resueltas" value={totals.resolved_chats ?? 0} accent="teal" />
          <Kpi icon={TrendingUp} label="% resuelto" value={totals.resolved_pct == null ? "sin valoraciones" : `${totals.resolved_pct}%`} accent="teal" />
          <Kpi icon={Activity} label="Sin valorar" value={totals.unrated_chats ?? 0} />
        </section>
        <p className="mt-2 text-[11px] text-slate-500">
          % resuelto = consultas en las que el usuario respondió «Sí» a «¿Le resolvió esto?» / consultas valoradas.
        </p>
        {/* Trend + Departments */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Card title="Tendencia de uso" subtitle={`Interacciones y mensajes · últimos ${window} ${window === 1 ? "mes" : "meses"}`}>
              <div className="h-[260px]" data-testid="trend-chart">
                {loading ? (
                  <div className="flex h-full items-center justify-center text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 200 }}>
                    <AreaChart data={trend} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gChats" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0D9488" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gMsgs" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0F172A" stopOpacity={0.18} />
                          <stop offset="100%" stopColor="#0F172A" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} width={36} />
                      <ReTooltip
                        contentStyle={{
                          background: "#0F172A",
                          border: "none",
                          borderRadius: 2,
                          color: "#F8FAFC",
                          fontSize: 12,
                        }}
                      />
                      <Area type="monotone" dataKey="messages" stroke="#0F172A" strokeWidth={1.6} fill="url(#gMsgs)" />
                      <Area type="monotone" dataKey="chats" stroke="#0D9488" strokeWidth={2} fill="url(#gChats)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </div>
          <div className="lg:col-span-4">
            <Card title="Por departamento" subtitle="Agentes generados">
              {loading ? (
                <div className="flex h-[220px] items-center justify-center text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : byDept.length === 0 ? (
                <p className="py-8 text-center text-sm italic text-slate-500">Aún no hay agentes generados.</p>
              ) : (
                <div className="space-y-3 py-2" data-testid="dept-breakdown">
                  {byDept.map((d) => (
                    <div key={d.department} className="flex items-center justify-between gap-3">
                      <span className="truncate font-display text-sm text-slate-800">{d.department}</span>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-32 bg-slate-100">
                          <div
                            className="h-full bg-accent-blue"
                            style={{ width: `${Math.min(100, (d.agents / Math.max(...byDept.map((x) => x.agents))) * 100)}%` }}
                          />
                        </div>
                        <span className="w-6 text-right font-mono text-xs font-semibold text-slate-900">{d.agents}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </section>

        {/* Leaderboards */}
        <section className={`mt-6 grid grid-cols-1 gap-6 ${seesSavings ? "lg:grid-cols-2" : ""}`}>
          <Card title="Más usados" subtitle="Top 5 por interacciones">
            <Leaderboard items={data?.leaderboard_by_chats || []} field="chats" suffix="" loading={loading} />
          </Card>
          {seesSavings && (
            <Card title="Mayor impacto" subtitle="Top 5 por ahorro estimado (dentro de cada moneda)">
              <Leaderboard items={data?.leaderboard_by_savings || []} field="usd_saved" prefix loading={loading} />
            </Card>
          )}
        </section>

        {/* All Agents grid */}
        <section className="mt-10">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="eyebrow">Inventario</p>
              <h2 className="mt-2 font-display text-2xl font-medium text-slate-900">
                Todos los agentes ({agents.length})
              </h2>
            </div>
            <Link to="/consulta">
              <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                Nuevo diagnóstico ABC <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="mt-8 flex items-center gap-3 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" /> Cargando agentes…
            </div>
          ) : agents.length === 0 ? (
            <div className="mt-8 border border-dashed border-slate-300 bg-white p-10 text-center">
              <Bot className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-4 font-display text-lg font-medium text-slate-900">
                Sin agentes todavía
              </p>
              <p className="mt-2 max-w-md mx-auto text-sm text-slate-600">
                Complete un diagnóstico ABC y genere los agentes IA por departamento para comenzar
                a ver métricas aquí.
              </p>
              <Link to="/consulta">
                <Button className="mt-6 rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-slate-800">
                  Iniciar diagnóstico
                </Button>
              </Link>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="agents-grid">
              {agents.map((a) => (
                <AgentCard key={a.slug} a={a} canEdit={isStaff} canEditMetrics={!!data?.can_edit_metrics} onMetricsSaved={reload} seesSavings={seesSavings} />
              ))}
            </div>
          )}
        </section>

        <p className="mt-12 text-[11px] uppercase tracking-[0.18em] text-amber-700">
          Estimaciones de ahorro basadas en configuración por agente (minutos por consulta · tarifa por hora en la moneda de su empresa). Ajustables en cada agente.
        </p>
      </main>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, prefix, suffix, accent = "navy" }) {
  const dot = accent === "teal" ? "bg-teal-600" : "bg-slate-900";
  return (
    <div className="relative overflow-hidden border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.7} />
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      </div>
      <p className="mt-5 font-display text-3xl font-semibold leading-none text-slate-900">
        {prefix || ""}{value}{suffix || ""}
      </p>
      <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="border border-slate-200 bg-white p-6">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-medium text-slate-900">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Leaderboard({ items, field, loading, prefix }) {
  if (loading) {
    return (
      <div className="flex h-[200px] items-center justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!items.length) {
    return <p className="py-6 text-center text-sm italic text-slate-500">Sin datos en la ventana seleccionada.</p>;
  }
  // fix 9: money bars are relative to agents of the SAME currency
  const cur = (i) => (prefix ? i.metrics?.currency || "USD" : "");
  const maxOf = (c) => Math.max(1, ...items.filter((i) => cur(i) === c).map((i) => i.metrics?.[field] || 0));
  return (
    <ol className="space-y-3">
      {items.map((a, i) => {
        const v = a.metrics?.[field] || 0;
        return (
          <li key={a.slug} className="flex items-center gap-3">
            <span className="w-5 font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
            <Link to={`/agentes/${a.slug}`} className="flex-1 truncate font-display text-sm text-slate-900 hover:text-accent-blue">
              {a.name}
              <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">{a.department}</span>
            </Link>
            <div className="relative h-1.5 w-28 bg-slate-100">
              <div className="absolute inset-y-0 left-0 bg-accent-blue" style={{ width: `${(v / maxOf(cur(a))) * 100}%` }} />
            </div>
            <span className="w-28 text-right font-mono text-xs font-semibold text-slate-900">
              {prefix ? `${cur(a)} ` : ""}{Number(v).toLocaleString("es-419", { maximumFractionDigits: 1 })}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function AgentCard({ a, canEdit, canEditMetrics, onMetricsSaved, seesSavings = true }) {
  const m = a.metrics || {};
  const trendData = (a.trend || []).map((t) => ({ ...t, label: MONTH_LABEL(t.month) }));
  return (
    <article
      className="group border border-slate-200 bg-white p-5 transition hover:border-teal-600/50 hover:shadow-sm"
      data-testid={`dash-agent-${a.slug}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center bg-slate-900 text-accent-blue-light">
            <Bot className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-accent-blue">{a.department}</p>
            <h3 className="mt-0.5 font-display text-base font-medium text-slate-900">{a.name}</h3>
          </div>
        </div>
        <Link to={`/agentes/${a.slug}/metricas`} className="text-[10px] uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900">
          Detalle <ArrowRight className="ml-0.5 inline h-3 w-3" />
        </Link>
      </div>

      <div className={`mt-4 grid ${seesSavings ? "grid-cols-3" : "grid-cols-2"} gap-2 text-center`}>
        <Stat label="Chats" value={m.chats ?? 0} />
        <Stat label="Mensajes" value={m.messages ?? 0} />
        {seesSavings && <Stat label={`${m.currency || a.metrics_config?.currency || "USD"} (est.)`} value={m.usd_saved == null ? "sin tarifa" : Number(m.usd_saved).toLocaleString("es-419", { maximumFractionDigits: 0 })} />}
      </div>
      <div className="mt-2 space-y-1">
        {seesSavings && <SavingsAssumptions
          key={`${a.metrics_config?.minutes_per_interaction}-${a.metrics_config?.hourly_rate_usd}`}
          slug={a.slug}
          config={a.metrics_config}
          canEdit={canEditMetrics}
          onSaved={onMetricsSaved}
          compact
        />}
        <p className="text-[11px] text-slate-500">
          % resuelto: {m.resolved_pct == null ? "sin valoraciones" : `${m.resolved_pct}% (${m.resolved_chats}/${m.rated_chats})`}
        </p>
      </div>

      <div className="mt-4 h-[60px]" data-testid={`spark-${a.slug}`}>
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 200 }}>
          <BarChart data={trendData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <Bar dataKey="chats" radius={[1, 1, 0, 0]}>
              {trendData.map((_, i) => (
                <Cell key={i} fill={i === trendData.length - 1 ? "#0D9488" : "#CBD5E1"} />
              ))}
            </Bar>
            <ReTooltip
              cursor={{ fill: "rgba(15,23,42,0.04)" }}
              contentStyle={{
                background: "#0F172A",
                border: "none",
                borderRadius: 2,
                color: "#F8FAFC",
                fontSize: 11,
                padding: 6,
              }}
              labelFormatter={() => ""}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <Users className="h-3 w-3" /> {a.prompts_count} prompts
        </span>
        <span className="inline-flex items-center gap-1">
          <Layers className="h-3 w-3" /> {a.tools_enabled} herramientas
        </span>
        <span className="inline-flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> {Math.round(m.starter_coverage_pct || 0)}%
        </span>
      </div>

      <div className="mt-4 flex gap-2">
        <Link to={`/agentes/${a.slug}`} className="flex-1">
          <Button className="w-full rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.18em] text-white hover:bg-slate-800">
            Abrir
          </Button>
        </Link>
        {canEdit && (
          <Link to={`/agentes/${a.slug}/editar`}>
            <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.18em] text-slate-700 hover:bg-slate-100">
              Editar
            </Button>
          </Link>
        )}
      </div>
    </article>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-100 bg-slate-50/60 px-2 py-2">
      <p className="font-display text-base font-semibold text-slate-900">{value}</p>
      <p className="text-[9px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
    </div>
  );
}
